import { Background } from '@app/shared/models/background';
import { Customization } from '@app/shared/models/customization';
import { Logo } from '@app/shared/models/logo';
import { createAction, props } from '@ngrx/store';
import { GetDomainCustomizationOutput } from '@smash-sdk/domain/ejs/01-2024';

// A minimal, non-optional shape for a queue entry pending create/attach -- `id` is load-bearing
// and required on purpose: customization.reducer.ts's 2026-09-15 incident (see the comment at
// customization.reducer.ts:222-227) crashed on `.id.startsWith('local_')` against exactly this
// kind of loosely-typed placeholder item once it was missing an id. Not the full Background/Logo
// model (see the comment on backgroundsToCreate below) -- only `id` is guaranteed; every other
// field this queue's own consumers read (sourceUrl, thumbnail, type -- customization.effects.ts's
// create-list builder and withRealCreatedIds/withRealCreatedId) stays untyped via the index
// signature, since a real Background/Logo record carries them but this local placeholder shape
// does not promise which ones (D10, docs/ultra-review/2026-09-16-sso-and-card-error-
// investigation-delta/REPORT.md).
export interface CustomizationQueueItem {
  id: string;
  [key: string]: unknown;
}

// Team
export const loadCustomizationReceiver = createAction(
  '[Customization] Load Customization Receiver',
);

export const loadCustomizationReceiverSuccess = createAction(
  '[Customization] Load Customization Receiver Success',
  props<{ customization: Customization }>()
);

export const loadCustomizationReceiverFailure = createAction(
  '[Customization] Load Customization Receiver Failure',
  props<{ error: any }>()
);

export const loadTeamBackgrounds = createAction(
  '[Customization] Load Team Backgrounds',
);

export const loadTeamBackgroundsSuccess = createAction(
  '[Customization] Load Team Backgrounds Success',
  props<{ backgrounds: Background[] }>()
);

export const loadTeamBackgroundsFailure = createAction(
  '[Customization] Load Team Backgrounds Failure',
  props<{ error: any }>()
);

export const loadTeamLogos = createAction(
  '[Customization] Load Team Logos',
);

export const loadTeamLogosSuccess = createAction(
  '[Customization] Load Team Logos Success',
  props<{ logos: Logo[] }>()
);

export const loadTeamLogosFailure = createAction(
  '[Customization] Load Team Logos Failure',
  props<{ error: any }>()
);

// Edition
export const selectBackground = createAction(
  '[Customization] Select background',
  props<{ background: GetDomainCustomizationOutput['customization']['background'] }>()
);

export const selectLogo = createAction(
  '[Customization] Select logo',
  props<{ logo: GetDomainCustomizationOutput['customization']['logo'] }>()
);

export const addTeamLogo = createAction(
  '[Customization] Add Team Logo',
  props<{ image?: any[], url?: string }>()
);

export const addTeamLogoSuccess = createAction(
  '[Customization] Add Team Logo Success',
  props<{ logo: Logo }>()
);

export const addTeamLogoFailure = createAction(
  '[Customization] Add Team Logo Failure',
  props<{ error: any }>()
);

export const addTeamBackgroundImage = createAction(
  '[Customization] Add Team Background Image',
  props<{ image?: any[], url?: string }>()
);

export const addTeamBackgroundImageSuccess = createAction(
  '[Customization] Add Team Background Image Success',
  props<{ background: GetDomainCustomizationOutput['customization']['background'] }>()
);

export const addTeamBackgroundImageFailure = createAction(
  '[Customization] Add Team Background Image Failure',
  props<{ error: any }>()
);

export const addTeamBackgroundVideo = createAction(
  '[Customization] Add Team Background Video',
  props<{ url: string }>()
);

export const addTeamBackgroundVideoSuccess = createAction(
  '[Customization] Add Team Background Video Success',
  props<{ background: Background }>()
);

export const addTeamBackgroundVideoFailure = createAction(
  '[Customization] Add Team Background Video Failure',
  props<{ error: any }>()
);

export const updateTeamCustomization = createAction(
  '[Customization] Update Team Customization',
);

export const updateCustomizationSuccess = createAction(
  '[Customization] Update Customization Success',
);

export const updateTeamCustomizationFailure = createAction(
  '[Customization] Update Team Customization Failure',
  // The four `*Succeeded` flags say which of the four pending-asset stages (each its own
  // independent HTTP call, or group of calls) is known to have completed on the server before
  // the failure -- so the reducer can drop only those from the retry queues instead of
  // blanket-clearing all four on any failure (which risks re-submitting a create/delete that
  // never reached the server, or silently orphaning one that did but whose stage partially
  // failed). Undefined means "not reached / not known" and is treated as not-safe-to-clear.
  props<{
    error: any;
    createdBackgroundsSucceeded?: boolean;
    createdLogosSucceeded?: boolean;
    deletedBackgroundsSucceeded?: boolean;
    deletedLogosSucceeded?: boolean;
    // Present whenever something was pending create this round and its own create call
    // succeeded -- whether a SIBLING create's failure pre-empted the attach step, or create fully
    // succeeded and the ATTACH step failed afterwards (Finding 1, attach-stage-gap follow-up
    // review) -- so the item cannot be reported as fully done via the `Succeeded` flags above but
    // must not be cleared either. Carries the pending-create queue with that item's local id
    // already swapped for its real, server-assigned one (see
    // customization.effects.ts#withRealCreatedIds), so a retry skips re-creating it and goes
    // straight to attach instead of duplicating it server-side. Undefined only when nothing was
    // pending to create in the first place, in which case the `Succeeded` flags above govern as
    // before.
    // CustomizationQueueItem[] (not the full Background/Logo model, and not `any[]`): this queue
    // holds locally-shaped pending items (plain `{ id, sourceUrl, ... }`), not full Background
    // records, so reusing that model type here would be a mismatch (D10, docs/ultra-review/
    // 2026-09-16-sso-and-card-error-investigation-delta/REPORT.md).
    backgroundsToCreate?: CustomizationQueueItem[];
    // Same reasoning as backgroundsToCreate above, mirroring State#logosToCreate.
    logosToCreate?: CustomizationQueueItem[];
    // Present only alongside a defined backgroundsToCreate override above, when the swapped
    // real id also applies to the currently selected/edited background --
    // customizationToUpdate.background and selectedBackground are set from the very same object
    // reference the queue holds at first-add time (see addTeamBackgroundImageToCreateSuccess,
    // customization.reducer.ts), so without this override they would keep the stale local id
    // forever: the next Save would still see a 'local_'-prefixed id despite nothing being pending
    // to (re)create, and read past the end of an EMPTY create-response array (D1,
    // docs/ultra-review/2026-09-16-sso-and-card-error-investigation-delta/REPORT.md). Undefined
    // in every other failure path.
    backgroundOnFailure?: CustomizationQueueItem;
    // Same reasoning as backgroundOnFailure above, for the logo side.
    logoOnFailure?: CustomizationQueueItem;
    // Same reasoning as backgroundsToCreate/logosToCreate above, mirrored on the delete side (F4,
    // ultra-review finding, user-confirmed 2026-09-22): present whenever something was queued for
    // deletion this round and at least one of those items' own delete call failed -- carries the
    // delete queue with every item the server confirmed deleted already dropped (see
    // customization.effects.ts#withRemainingToDelete), so a retry resends a DELETE only for the
    // item(s) that actually failed instead of blanket-keeping (or blanket-clearing) the whole
    // batch. Undefined only when nothing was pending to delete in the first place, in which case
    // the `Succeeded` flags above govern as before. Background[] (the full model, not
    // CustomizationQueueItem): mirrors State#backgroundsToDelete's own type.
    backgroundsToDelete?: Background[];
    // Same reasoning as backgroundsToDelete above, for the logo side.
    logosToDelete?: Logo[];
  }>()
);

export const addTeamBackgroundImageToCreate = createAction(
  '[Customization] Add Team Background Image To Create',
  props<{ image: any }>()
);

// Background, not CustomizationQueueItem (adversarial-review follow-up, 2026-09-17): the sole
// producer, customization.effects.ts's addTeamBackgroundImageToCreate$, always builds the full
// { id, thumbnail, sourceUrl, sourceId, type } shape before dispatching this action, so the
// payload is never actually a bare id-only placeholder here -- unlike backgroundsToCreate/
// updateTeamCustomizationFailure above, which genuinely can carry one. Typing this as the loose
// CustomizationQueueItem forced customization.reducer.ts's handler to cast it straight to
// Background with `as unknown as`, which silences the compiler at exactly the boundary meant to
// catch a future dispatch of this action with a bare `{ id }` -- defeating the type safety
// CustomizationQueueItem exists to add. Background is the correct, non-loose type here.
export const addTeamBackgroundImageToCreateSuccess = createAction(
  '[Customization] Add Team Background Image To Create Success',
  props<{ background: Background }>()
);

export const addTeamBackgroundImageToCreateFailure = createAction(
  '[Customization] Add Team Background Image To Create Failure',
  props<{ error: any }>()
);

export const addTeamBackgroundVideoToCreate = createAction(
  '[Customization] Add Team Background Video To Create',
  props<{ video: any }>()
);

export const addTeamBackgroundVideoToCreateSuccess = createAction(
  '[Customization] Add Team Background Video To Create Success',
  props<{ background: any }>()
);

export const addTeamBackgroundToDelete = createAction(
  '[Customization] Add Team Background To Delete',
  props<{ background: Background }>()
);

export const addTeamLogoToCreate = createAction(
  '[Customization] Add Team Logo To Create',
  props<{ logo: any }>()
);

// Logo, not CustomizationQueueItem -- same reasoning as addTeamBackgroundImageToCreateSuccess
// above: the sole producer, customization.effects.ts's addTeamLogoToCreate$, always builds the
// full { id, thumbnail, sourceUrl, sourceId, type } shape before dispatching this action.
export const addTeamLogoToCreateSuccess = createAction(
  '[Customization] Add Team Logo To Create Success',
  props<{ logo: Logo }>()
);

export const addTeamLogoToCreateFailure = createAction(
  '[Customization] Add Team Logo To Create Failure',
  props<{ error: any }>()
);

export const addTeamLogoToDelete = createAction(
  '[Customization] Add Team Logo To Delete',
  props<{ logo: Logo }>()
);


export const setCustomizationToUpdate = createAction(
  '[Customization] Set customization to Update',
  props<{ target: 'receiver' | 'sender' }>()
);

